import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../../util/reactIntl';
import { Heading, NamedLink, IconCategoryLink } from '../../../components';
import { categoryData } from '../../../config/categoryData';

import css from './CategoryPanel.module.css';

/**
 * CategoryPanel component that displays category icons in a responsive layout
 * Mobile: horizontal slider with dots
 * Desktop (--viewportMedium+): grid layout
 * 
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class
 * @param {string} [props.rootClassName] - Custom class that overrides the default class
 * @param {Array} [props.categories] - Array of category objects (defaults to categoryData)
 * @returns {JSX.Element} Category panel with responsive category icons
 */
const CategoryPanel = props => {
  const {
    className,
    rootClassName,
    categories = categoryData,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  // Don't render if no categories
  if (!categories || categories.length === 0) {
    return null;
  }

  return (
    <div className={classes}>
      {/* Panel Header */}
      <div className={css.header}>
        {/* Left side - Title */}
        <div className={css.headerLeft}>
          <Heading as="h2" rootClassName={css.title}>
            <FormattedMessage id="CategoryPanel.title" />
          </Heading>
        </div>

        {/* Right side - Explore link */}
        <div className={css.headerRight}>
          {/* Simple "Explore all" button for mobile/tablet */}
          {/* <NamedLink
            name="SearchPage"
            className={css.exploreAllButton}
          >
            <FormattedMessage id="CategoryPanel.seeAll" />
          </NamedLink> */}

          {/* Full navigation for medium+ screens */}
          <div className={css.fullNavigation}>
            <NamedLink
              name="SearchPage"
              className={css.exploreLink}
            >
              <FormattedMessage id="CategoryPanel.exploreAll" />
            </NamedLink>
          </div>
        </div>
      </div>

      {/* Categories Container */}
      <div className={css.categoriesContainer}>
        {/* Mobile/Tablet: Horizontal scroll */}
        <div className={css.mobileCarousel}>
          {categories.map((category) => (
            <div key={category.id} className={css.mobileItemWrapper}>
              <IconCategoryLink category={category} />
            </div>
          ))}
        </div>

        {/* Desktop: Grid layout */}
        <div className={css.desktopGrid}>
          {categories.map((category) => (
            <div key={category.id} className={css.gridItemWrapper}>
              <IconCategoryLink category={category} />
            </div>
          ))}
        </div>


      </div>
    </div>
  );
};

CategoryPanel.propTypes = {
  className: PropTypes.string,
  rootClassName: PropTypes.string,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      src: PropTypes.string.isRequired,
    })
  ),
};

export default CategoryPanel;
